import { AppPlatform, AppStoreType, AutoCherryPickStrategy, Automation, AutomationType } from '../types';
import { RecursiveStringifyEnums } from './buildDistro';

export const otaAutomations: RecursiveStringifyEnums<Automation[]> = [
    {
        data: {
            strategy: AutoCherryPickStrategy.SingleInSequence,
            shouldNotifyPRAuthors: false,
            token: null,
            allowFixesWithToken: false,
            requiresFixApprovalToCherryPick: false,
        },
        description:
            'For any merged PR whose title contains the text pattern specified below, Runway will cherry-pick its commits from the working branch to the relevant release branch. Note that Runway will always open a PR containing the cherry-picked commits and targeting the release branch.',
        enabled: false,
        id: 'AutoCherryPickFromWorkingBranch',
        stepConfigId: null,
        type: 'AutoCherryPickFromWorkingBranch',
        isNew: false,
        isBeta: false,
        name: 'Pull (cherry-pick) fixes into the release',
        requiredCapabilities: [['versionControl']],
        supportedPlatforms: ['ios', 'android', 'ios-sdk', 'android-sdk', 'react-native-ota', 'tvos'],
        integrationInstallationIds: [],
        unsupportedIntegrations: [],
        platform: 'react-native-ota',
        appStoreType: '',
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: null,
        description:
            'Runway will mark releases as completed in your project management tool at the end of each release cycle.',
        enabled: false,
        id: 'AutoCompleteIssueTrackingReleases',
        stepConfigId: null,
        type: 'AutoCompleteIssueTrackingReleases',
        isNew: false,
        isBeta: false,
        name: 'Mark releases as completed in project management tool',
        requiredCapabilities: [['featureManagement']],
        supportedPlatforms: ['ios', 'android', 'ios-sdk', 'android-sdk', 'react-native-ota', 'tvos'],
        integrationInstallationIds: [],
        unsupportedIntegrations: ['asana', 'azure-it', 'linear', 'pivotal', 'shortcut'],
        platform: 'react-native-ota',
        appStoreType: '',
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: null,
        description:
            'Runway will create a new release record for your next version when your current release is kicked off. This will assign a release pilot and provide a selectable upcoming release via the versions timeline in App Home.',
        enabled: true,
        id: 'AutoCreateRunwayReleases',
        stepConfigId: null,
        type: 'AutoCreateRunwayReleases',
        isNew: false,
        isBeta: false,
        name: 'Create upcoming version in Runway when current release is kicked off',
        requiredCapabilities: [],
        supportedPlatforms: ['ios', 'android', 'ios-sdk', 'android-sdk', 'react-native-ota', 'tvos'],
        integrationInstallationIds: [],
        unsupportedIntegrations: [],
        platform: 'react-native-ota',
        appStoreType: '',
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: {
            fallbackToHeadIfNeeded: false,
        },
        description:
            'Runway will tag your production release commit at the end of each release cycle, and generate a summary of all work going out with the release.',
        enabled: false,
        id: 'AutoTagReleases',
        stepConfigId: null,
        type: 'AutoTagReleases',
        isNew: false,
        isBeta: false,
        name: 'Tag releases at the end of the release cycle',
        requiredCapabilities: [['appStore'], ['buildSystem'], ['versionControl']],
        supportedPlatforms: ['ios', 'android', 'ios-sdk', 'android-sdk', 'react-native-ota', 'tvos'],
        integrationInstallationIds: [],
        unsupportedIntegrations: [],
        platform: 'react-native-ota',
        appStoreType: '',
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: null,
        description:
            'Runway creates pull requests to fulfill certain tasks (e.g. bumping version in code). If enabled, Runway will attempt to merge these PRs automatically, once any status checks or approvals are satisfied.',
        enabled: false,
        id: 'AutoMergeRunwayPRs',
        stepConfigId: null,
        type: 'AutoMergeRunwayPRs',
        isNew: false,
        isBeta: false,
        name: 'Merge pull requests opened by Runway',
        requiredCapabilities: [['versionControl']],
        supportedPlatforms: ['ios', 'android', 'ios-sdk', 'android-sdk', 'react-native-ota', 'tvos'],
        integrationInstallationIds: [],
        unsupportedIntegrations: [],
        platform: 'react-native-ota',
        appStoreType: '',
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: {
            strategy: 'onReleaseCompletion',
        },
        description:
            'Runway will backmerge changes from the release branch to the working branch (and any other upcoming release branches).',
        enabled: false,
        id: 'AutoBackmergeReleaseBranches',
        stepConfigId: null,
        type: 'AutoBackmergeReleaseBranches',
        isNew: false,
        isBeta: false,
        name: 'Backmerge changes from the release branch',
        requiredCapabilities: [['versionControl']],
        supportedPlatforms: ['ios', 'android', 'ios-sdk', 'android-sdk', 'react-native-ota', 'tvos'],
        integrationInstallationIds: [],
        unsupportedIntegrations: [],
        platform: 'react-native-ota',
        appStoreType: '',
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: null,
        description:
            'Runway will add appropriate labels and/or fix versions when they are missing from tickets that are associated with the release. These label and fix version patterns are defined in App Settings.',
        enabled: false,
        id: 'AutoAddMissingFAs',
        stepConfigId: null,
        type: 'AutoAddMissingFAs',
        isNew: false,
        isBeta: false,
        name: 'Add missing labels or fix versions to tickets',
        requiredCapabilities: [['featureManagement'], ['versionControl']],
        supportedPlatforms: ['ios', 'android', 'ios-sdk', 'android-sdk', 'react-native-ota', 'tvos'],
        integrationInstallationIds: [],
        unsupportedIntegrations: [],
        platform: 'react-native-ota',
        appStoreType: '',
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: {
            allowKickoffWhenPreviousNotComplete: false,
            shouldCommitAndPushDirectlyToTargetBranch: false,
        },
        description:
            'Runway will create a release branch off of your working branch for the next release on the scheduled date.',
        enabled: true,
        id: 'AutoKickoff',
        stepConfigId: null,
        type: 'AutoKickoff',
        isNew: false,
        isBeta: false,
        name: 'Kick off release on target date',
        requiredCapabilities: [['versionControl']],
        supportedPlatforms: ['ios', 'android', 'ios-sdk', 'android-sdk', 'react-native-ota', 'tvos'],
        integrationInstallationIds: [],
        unsupportedIntegrations: [],
        platform: 'react-native-ota',
        appStoreType: '',
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: null,
        description:
            "Runway will automatically create new Slack channel(s) for each release. If a tokenized channel name (e.g. release-{version}) is configured as either the 'Primary channel' for the Slack integration, or as a channel override for any notification in settings, Runway will create a new channel matching the tokenized channel name for each active release if needed.",
        enabled: true,
        id: 'AutoCreateReleaseChannels',
        stepConfigId: null,
        type: 'AutoCreateReleaseChannels',
        isNew: false,
        isBeta: false,
        name: 'Create release-specific Slack or Microsoft Teams channel(s)',
        requiredCapabilities: [['notifications']],
        supportedPlatforms: ['ios', 'android', 'ios-sdk', 'android-sdk', 'react-native-ota', 'tvos'],
        integrationInstallationIds: [],
        unsupportedIntegrations: [],
        platform: 'react-native-ota',
        appStoreType: '',
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: null,
        description: 'Runway will trigger the configured CI build workflow after release is kicked off.',
        enabled: true,
        id: 'AutoKickoffTriggerWorkflow',
        stepConfigId: null,
        type: 'AutoKickoffTriggerWorkflow',
        isNew: false,
        isBeta: false,
        name: 'Trigger workflow after release kickoff',
        requiredCapabilities: [['buildSystem'], ['versionControl']],
        supportedPlatforms: ['ios', 'android', 'ios-sdk', 'android-sdk', 'react-native-ota', 'tvos'],
        integrationInstallationIds: [],
        unsupportedIntegrations: [],
        platform: 'react-native-ota',
        appStoreType: '',
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: { userGroupHandle: '' },
        description:
            'Runway will create and keep updated a Slack user group whose members are the release pilot for the active/next-up release. Specify the user group handle (e.g. runway-active-pilot) in the settings below.',
        enabled: false,
        id: 'AutoSyncSlackPilotUserGroup',
        stepConfigId: null,
        type: 'AutoSyncSlackPilotUserGroup',
        isNew: true,
        isBeta: false,
        name: 'Sync Slack pilot user group',
        requiredCapabilities: [['notifications']],
        supportedPlatforms: ['ios', 'android', 'ios-sdk', 'android-sdk', 'react-native-ota', 'tvos'],
        integrationInstallationIds: [],
        unsupportedIntegrations: ['microsoft-teams'],
        platform: 'react-native-ota',
        appStoreType: '',
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: null,
        description:
            'Runway will add and remove users to Slack groups based on their user groups in Runway. You can configure the mapping of user groups to Slack groups from App settings > Team > Slack groups sync',
        enabled: true,
        id: 'AutoSyncSlackGroups',
        stepConfigId: null,
        type: 'AutoSyncSlackGroups',
        isNew: true,
        isBeta: false,
        name: 'Sync Slack groups',
        requiredCapabilities: [['notifications']],
        supportedPlatforms: ['ios', 'android', 'ios-sdk', 'android-sdk', 'react-native-ota', 'tvos'],
        integrationInstallationIds: [],
        unsupportedIntegrations: ['microsoft-teams'],
        platform: 'react-native-ota',
        appStoreType: '',
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: {
            strategy: 'AutoCreateTestRunsWithoutTestPlans',
            testRunsFilterSettings: [],
            testPlansFilterSettings: [],
        },
        description: `Runway will create test runs (and if needed, test plans) for each new release when it's kicked off. The test runs and test plans from the previous release will be used as a template for test run and test plan creation.`,
        enabled: true,
        id: 'AutoCreateTestRuns',
        stepConfigId: null,
        type: 'AutoCreateTestRuns',
        isNew: true,
        isBeta: false,
        name: 'Create regression test runs',
        requiredCapabilities: [['regressionTesting']],
        supportedPlatforms: ['ios', 'android', 'ios-sdk', 'android-sdk', 'react-native-ota', 'tvos'],
        integrationInstallationIds: [],
        unsupportedIntegrations: ['xray'],
        platform: 'react-native-ota',
        appStoreType: '',
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: null,
        description:
            "Runway will post relevant build info as a comment on project management tickets when code that's associated to the ticket generates a CI build.",
        enabled: true,
        id: 'AutoPostBuildInfoOnPMTickets',
        stepConfigId: null,
        type: 'AutoPostBuildInfoOnPMTickets',
        isNew: true,
        isBeta: true,
        name: 'Post build info to project management tickets',
        requiredCapabilities: [['buildSystem', 'featureManagement']],
        supportedPlatforms: ['ios', 'tvos', 'android', 'android-sdk', 'ios-sdk', 'react-native-ota'],
        integrationInstallationIds: [],
        unsupportedIntegrations: ['asana', 'azure-it', 'linear', 'pivotal', 'shortcut', 'monday'],
        platform: 'react-native-ota',
        appStoreType: '',
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: {
            shouldCommitAndPushDirectlyToTargetBranch: false,
            strategy: 'AutoBumpPostKickoffToNextVersion',
        },
        description:
            'Runway will bump the version on your working branch or release branch according to the strategy set for the app.',
        enabled: true,
        id: 'AutoBumpVersion',
        stepConfigId: null,
        type: 'AutoBumpVersion',
        isNew: false,
        isBeta: false,
        name: 'Bump version',
        requiredCapabilities: [['versionControl']],
        supportedPlatforms: ['ios', 'tvos', 'android'],
        integrationInstallationIds: [],
        platform: 'android',
        appStoreType: 'GooglePlay',
        unsupportedIntegrations: [],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
];

export const androidAutomations: RecursiveStringifyEnums<Automation[]> = [
    {
        id: 'AutoResignRollbackBuild',
        type: AutomationType.AutoResignRollbackBuild,
        stepConfigId: null,
        name: 'Re-sign build for rollbacks',
        description:
            "Runway will take the build from the source version, bump the version, update the build number, and re-sign it so it's ready for app store upload.",
        additionalInfo: null,
        requiredCapabilities: [['buildSystem'], ['appStore']],
        unsupportedIntegrations: ['huawei', 'samsung'],
        supportedPlatforms: ['ios', 'tvos', 'android', 'wearos', 'android-tv'],
        integrationInstallationIds: [],
        platform: 'android',
        appStoreType: 'GooglePlay',
        enabled: true,
        data: null,
        isNew: false,
        isBeta: true,
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: {
            strategy: AutoCherryPickStrategy.Cumulative,
            shouldNotifyPRAuthors: true,
            token: '[fix-{version}]',
            allowFixesWithToken: false,
            requiresFixApprovalToCherryPick: false,
        },
        description:
            'For any merged PR whose title contains the text pattern specified below, Runway will cherry-pick its commits from the working branch to the relevant release branch. Note that Runway will always open a PR containing the cherry-picked commits and targeting the release branch.',
        enabled: true,
        id: 'AutoCherryPickFromWorkingBranch',
        stepConfigId: null,
        type: 'AutoCherryPickFromWorkingBranch',
        isNew: false,
        isBeta: false,
        name: 'Pull (cherry-pick) fixes into the release',
        requiredCapabilities: [['versionControl']],
        supportedPlatforms: ['ios', 'android', 'ios-sdk', 'android-sdk', 'react-native-ota', 'tvos'],
        integrationInstallationIds: [],
        platform: 'android',
        appStoreType: 'GooglePlay',
        unsupportedIntegrations: [],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: {
            track: null,
        },
        description:
            'Based on your selection below, Runway will select an APK/AAB in the Play Console, and apply it to the Draft release on the production track. You can optionally choose to limit automatic selection to APKs/AABs that are already available on a specific testing track.',
        enabled: true,
        id: 'AutoSelectLatestBuild',
        stepConfigId: null,
        type: 'AutoSelectLatestBuild',
        isNew: false,
        isBeta: false,
        name: 'Add APK/AAB to the production track',
        requiredCapabilities: [['appStore']],
        supportedPlatforms: ['ios', 'tvos', 'android'],
        integrationInstallationIds: [],
        platform: 'android',
        appStoreType: 'GooglePlay',
        unsupportedIntegrations: [],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: null,
        description:
            'Runway will create a draft production release in the Play Console for the current release in Runway if needed, and keep the version name in sync if the version changes in Runway.',
        enabled: true,
        id: 'AutoCreateAppStoreVersions',
        stepConfigId: null,
        type: 'AutoCreateAppStoreVersions',
        isNew: false,
        isBeta: false,
        name: 'Create and sync draft production release in the Play Console to match current Runway release',
        requiredCapabilities: [['appStore']],
        supportedPlatforms: ['ios', 'tvos', 'android'],
        integrationInstallationIds: [],
        platform: 'android',
        appStoreType: 'GooglePlay',
        unsupportedIntegrations: [],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: null,
        description: 'Runway will apply beta testing notes to new beta builds as they become available.',
        enabled: false,
        id: 'AutoApplyBetaBuildNotes',
        stepConfigId: null,
        type: 'AutoApplyBetaBuildNotes',
        isNew: false,
        isBeta: false,
        name: 'Apply beta testing notes to new beta builds',
        requiredCapabilities: [['betaTesting']],
        supportedPlatforms: ['ios', 'android', 'ios-sdk', 'android-sdk', 'react-native-ota', 'tvos'],
        integrationInstallationIds: [],
        platform: 'android',
        appStoreType: 'GooglePlay',
        unsupportedIntegrations: ['amazon'],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: null,
        description: 'Runway will assign any new distributed builds to your default beta groups for testing.',
        enabled: false,
        id: 'AutoAssignBetaBuilds',
        stepConfigId: null,
        type: 'AutoAssignBetaBuilds',
        isNew: false,
        isBeta: false,
        name: 'Assign beta testing builds to default testing groups',
        requiredCapabilities: [['betaTesting']],
        supportedPlatforms: ['ios', 'tvos', 'android'],
        integrationInstallationIds: [],
        platform: 'android',
        appStoreType: 'GooglePlay',
        unsupportedIntegrations: [],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: null,
        description:
            'Runway will download all artifacts generated from your build workflow and provide direct download links to them from the Runway interface. Additionally, Runway will include artifact information in Slack build success notifications, including a link to find those downloads in Runway.',
        enabled: true,
        id: 'AutoDownloadArtifacts',
        stepConfigId: null,
        type: 'AutoDownloadArtifacts',
        isNew: false,
        isBeta: false,
        name: 'Enable artifact downloads',
        requiredCapabilities: [['buildSystem']],
        supportedPlatforms: ['ios', 'tvos', 'android'],
        integrationInstallationIds: [],
        platform: 'android',
        appStoreType: 'GooglePlay',
        unsupportedIntegrations: [
            'appcenter-ci',
            'apple-ci',
            'azure-ci',
            'buildkite',
            'codemagic',
            'generic-ci',
            'jenkins',
            'travis',
        ],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: null,
        description:
            'When tagging your production release commit, Runway will attach all artifacts generated from your active production build workflow as assets on your release record.',
        enabled: true,
        id: 'AutoUploadReleaseAssets',
        stepConfigId: null,
        type: 'AutoUploadReleaseAssets',
        isNew: false,
        isBeta: false,
        name: 'Attach build artifacts to VCS release record when tagging',
        requiredCapabilities: [['buildSystem'], ['versionControl']],
        supportedPlatforms: ['ios', 'tvos', 'android'],
        integrationInstallationIds: [],
        platform: 'android',
        appStoreType: 'GooglePlay',
        unsupportedIntegrations: [
            'appcenter-ci',
            'apple-ci',
            'azure-ci',
            'buildkite',
            'codemagic',
            'generic-ci',
            'jenkins',
            'travis',
            'bitbucket',
            'gitlab-vcs',
        ],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: {
            uploadRule: 'uploadNewestBuildOnly',
            uploadWorkflowSettings: [
                {
                    destinationInstallationId: 'betaTesting',
                    fileNames: ['demo.aab'],
                    workflowType: 'staging',
                },
            ],
        },
        description: 'Runway will upload build artifacts for distribution according to the configured rules.',
        enabled: true,
        id: 'AutoUploadBinaryArtifactToAppStore',
        stepConfigId: null,
        type: 'AutoUploadBinaryArtifactToAppStore',
        isNew: true,
        isBeta: false,
        name: 'Upload build artifacts for distribution',
        requiredCapabilities: [['appStore', 'betaTesting'], ['buildSystem']],
        supportedPlatforms: ['ios', 'tvos', 'android'],
        integrationInstallationIds: [],
        platform: 'android',
        appStoreType: 'GooglePlay',
        unsupportedIntegrations: [
            'appcenter-ci',
            'apple-ci',
            'azure-ci',
            'buildkite',
            'codemagic',
            'generic-ci',
            'jenkins',
            'travis',
            'appcenter-beta',
            'firebase-beta',
        ],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: null,
        description:
            'Runway will mark releases as completed in your project management tool at the end of each release cycle.',
        enabled: true,
        id: 'AutoCompleteIssueTrackingReleases',
        stepConfigId: null,
        type: 'AutoCompleteIssueTrackingReleases',
        isNew: false,
        isBeta: false,
        name: 'Mark releases as completed in project management tool',
        requiredCapabilities: [['featureManagement']],
        supportedPlatforms: ['ios', 'android', 'ios-sdk', 'android-sdk', 'react-native-ota', 'tvos'],
        integrationInstallationIds: [],
        platform: 'android',
        appStoreType: 'GooglePlay',
        unsupportedIntegrations: ['asana', 'azure-it', 'linear', 'pivotal', 'shortcut'],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: null,
        description:
            'Runway will apply release notes to new versions created in the Play Console. You can make updates to the text as needed before you submit your update for review.',
        enabled: true,
        id: 'AutoApplyMetadata',
        stepConfigId: null,
        type: 'AutoApplyMetadata',
        isNew: false,
        isBeta: false,
        name: 'Apply release notes to new releases in the Play Console',
        requiredCapabilities: [['appStore']],
        supportedPlatforms: ['ios', 'tvos', 'android'],
        integrationInstallationIds: [],
        platform: 'android',
        appStoreType: 'GooglePlay',
        unsupportedIntegrations: [],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: null,
        description:
            'Runway will create a new release record for your next version when your current release is kicked off. This will assign a release pilot and provide a selectable upcoming release via the versions timeline in App Home.',
        enabled: true,
        id: 'AutoCreateRunwayReleases',
        stepConfigId: null,
        type: 'AutoCreateRunwayReleases',
        isNew: false,
        isBeta: false,
        name: 'Create upcoming version in Runway when current release is kicked off',
        requiredCapabilities: [],
        supportedPlatforms: ['ios', 'android', 'ios-sdk', 'android-sdk', 'react-native-ota', 'tvos'],
        integrationInstallationIds: [],
        platform: 'android',
        appStoreType: 'GooglePlay',
        unsupportedIntegrations: [],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: {
            fallbackToHeadIfNeeded: false,
        },
        description:
            'Runway will tag your production release commit at the end of each release cycle, and generate a summary of all work going out with the release.',
        enabled: true,
        id: 'AutoTagReleases',
        stepConfigId: null,
        type: 'AutoTagReleases',
        isNew: false,
        isBeta: false,
        name: 'Tag releases at the end of the release cycle',
        requiredCapabilities: [['appStore'], ['buildSystem'], ['versionControl']],
        supportedPlatforms: ['ios', 'android', 'ios-sdk', 'android-sdk', 'react-native-ota', 'tvos'],
        integrationInstallationIds: [],
        platform: 'android',
        appStoreType: 'GooglePlay',
        unsupportedIntegrations: [],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: null,
        description:
            'Runway creates pull requests to fulfill certain tasks (e.g. bumping version in code). If enabled, Runway will attempt to merge these PRs automatically, once any status checks or approvals are satisfied.',
        enabled: true,
        id: 'AutoMergeRunwayPRs',
        stepConfigId: null,
        type: 'AutoMergeRunwayPRs',
        isNew: false,
        isBeta: false,
        name: 'Merge pull requests opened by Runway',
        requiredCapabilities: [['versionControl']],
        supportedPlatforms: ['ios', 'android', 'ios-sdk', 'android-sdk', 'react-native-ota', 'tvos'],
        integrationInstallationIds: [],
        platform: 'android',
        appStoreType: 'GooglePlay',
        unsupportedIntegrations: [],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: {
            strategy: 'onReleaseCompletion',
        },
        description:
            'Runway will backmerge changes from the release branch to the working branch (and any other upcoming release branches).',
        enabled: false,
        id: 'AutoBackmergeReleaseBranches',
        stepConfigId: null,
        type: 'AutoBackmergeReleaseBranches',
        isNew: false,
        isBeta: false,
        name: 'Backmerge changes from the release branch',
        requiredCapabilities: [['versionControl']],
        supportedPlatforms: ['ios', 'android', 'ios-sdk', 'android-sdk', 'react-native-ota', 'tvos'],
        integrationInstallationIds: [],
        platform: 'android',
        appStoreType: 'GooglePlay',
        unsupportedIntegrations: [],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: null,
        description:
            'Runway will add appropriate labels and/or fix versions when they are missing from tickets that are associated with the release. These label and fix version patterns are defined in App Settings.',
        enabled: true,
        id: 'AutoAddMissingFAs',
        stepConfigId: null,
        type: 'AutoAddMissingFAs',
        isNew: false,
        isBeta: false,
        name: 'Add missing labels or fix versions to tickets',
        requiredCapabilities: [['featureManagement'], ['versionControl']],
        supportedPlatforms: ['ios', 'android', 'ios-sdk', 'android-sdk', 'react-native-ota', 'tvos'],
        integrationInstallationIds: [],
        platform: 'android',
        appStoreType: 'GooglePlay',
        unsupportedIntegrations: [],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: {
            allowKickoffWhenPreviousNotComplete: true,
            shouldCommitAndPushDirectlyToTargetBranch: false,
        },
        description:
            'Runway will create a release branch off of your working branch for the next release on the scheduled date.',
        enabled: true,
        id: 'AutoKickoff',
        stepConfigId: null,
        type: 'AutoKickoff',
        isNew: false,
        isBeta: false,
        name: 'Kick off release on target date',
        requiredCapabilities: [['versionControl']],
        supportedPlatforms: ['ios', 'android', 'ios-sdk', 'android-sdk', 'react-native-ota', 'tvos'],
        integrationInstallationIds: [],
        platform: 'android',
        appStoreType: 'GooglePlay',
        unsupportedIntegrations: [],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: {
            submitStrategy: 'onTargetDate',
        },
        description: 'Runway will submit your selected build for review if all previous steps are ready (green).',
        enabled: true,
        id: 'AutoSubmit',
        stepConfigId: null,
        type: 'AutoSubmit',
        isNew: false,
        isBeta: false,
        name: 'Submit app for review',
        requiredCapabilities: [['appStore']],
        supportedPlatforms: ['ios', 'tvos', 'android'],
        integrationInstallationIds: [],
        platform: 'android',
        appStoreType: 'GooglePlay',
        unsupportedIntegrations: [],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: null,
        description: 'Runway will promote any newly uploaded APKs/AABs to your testing track and start the rollout.',
        enabled: true,
        id: 'AutoPromoteBuildsToTestTrack',
        stepConfigId: null,
        type: 'AutoPromoteBuildsToTestTrack',
        isNew: false,
        isBeta: false,
        name: 'Promote new builds to testing track',
        requiredCapabilities: [['betaTesting']],
        supportedPlatforms: ['android'],
        integrationInstallationIds: [],
        platform: 'android',
        appStoreType: 'GooglePlay',
        unsupportedIntegrations: ['apple-beta', 'appcenter-beta', 'firebase-beta'],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: {
            shouldCommitAndPushDirectlyToTargetBranch: false,
            strategy: 'AutoBumpPostKickoffToNextVersion',
        },
        description:
            'Runway will bump the version on your working branch or release branch according to the strategy set for the app.',
        enabled: true,
        id: 'AutoBumpVersion',
        stepConfigId: null,
        type: 'AutoBumpVersion',
        isNew: false,
        isBeta: false,
        name: 'Bump version',
        requiredCapabilities: [['versionControl']],
        supportedPlatforms: ['ios', 'tvos', 'android'],
        integrationInstallationIds: [],
        platform: 'android',
        appStoreType: 'GooglePlay',
        unsupportedIntegrations: [],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: {
            adoptionThreshold: null,
            monitoringConditions: [],
            phasedReleaseDayThreshold: null,
            stagedRolloutPercentageThreshold: null,
        },
        description:
            'Runway will roll out your update to all users early if all of your defined conditions have been met, and your selected health metrics stay within your defined health threshold',
        enabled: true,
        id: 'AutoPromoteStablePhasedReleases',
        stepConfigId: null,
        type: 'AutoPromoteStablePhasedReleases',
        isNew: false,
        isBeta: false,
        name: 'Release stable phased releases to 100% of users',
        requiredCapabilities: [['appStore', 'productAnalytics', 'stabilityMonitoring']],
        supportedPlatforms: ['ios', 'tvos', 'android'],
        integrationInstallationIds: [],
        platform: 'android',
        appStoreType: 'GooglePlay',
        unsupportedIntegrations: ['amazon'],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: {
            adoptionThreshold: null,
            monitoringConditions: [],
            phasedReleaseDayThreshold: null,
            stagedRolloutPercentageThreshold: null,
        },
        description:
            'Runway will halt your phased rollout if all of your defined conditions have been met, and any of your selected health metrics become unhealthy.',
        enabled: true,
        id: 'AutoHaltUnstablePhasedReleases',
        stepConfigId: null,
        type: 'AutoHaltUnstablePhasedReleases',
        isNew: false,
        isBeta: false,
        name: 'Halt unstable phased releases',
        requiredCapabilities: [['appStore', 'productAnalytics', 'stabilityMonitoring']],
        supportedPlatforms: ['ios', 'tvos', 'android'],
        integrationInstallationIds: [],
        platform: 'android',
        appStoreType: 'GooglePlay',
        unsupportedIntegrations: ['amazon'],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: null,
        description:
            "Runway will automatically create new Slack channel(s) for each release. If a tokenized channel name (e.g. release-{version}) is configured as either the 'Primary channel' for the Slack integration, or as a channel override for any notification in settings, Runway will create a new channel matching the tokenized channel name for each active release if needed.",
        enabled: false,
        id: 'AutoCreateReleaseChannels',
        stepConfigId: null,
        type: 'AutoCreateReleaseChannels',
        isNew: false,
        isBeta: false,
        name: 'Create release-specific Slack or Microsoft Teams channel(s)',
        requiredCapabilities: [['notifications']],
        supportedPlatforms: ['ios', 'android', 'ios-sdk', 'android-sdk', 'react-native-ota', 'tvos'],
        integrationInstallationIds: [],
        platform: 'android',
        appStoreType: 'GooglePlay',
        unsupportedIntegrations: [],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: null,
        description: 'Runway will trigger the configured CI build workflow after release is kicked off.',
        enabled: false,
        id: 'AutoKickoffTriggerWorkflow',
        stepConfigId: null,
        type: 'AutoKickoffTriggerWorkflow',
        isNew: false,
        isBeta: false,
        name: 'Trigger workflow after release kickoff',
        requiredCapabilities: [['buildSystem'], ['versionControl']],
        supportedPlatforms: ['ios', 'android', 'ios-sdk', 'android-sdk', 'react-native-ota', 'tvos'],
        integrationInstallationIds: [],
        platform: 'android',
        appStoreType: 'GooglePlay',
        unsupportedIntegrations: [],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: {
            allowDeleteUsers: false,
        },
        description:
            'Users in Runway will automatically be invited to Google Play Console, with the appropriate permissions granted. Please note: this automation requires that the service account in use by Runway have Admin permissions.',
        enabled: false,
        id: 'AutoSyncAppStoreUsers',
        stepConfigId: null,
        type: 'AutoSyncAppStoreUsers',
        isNew: false,
        isBeta: false,
        name: 'Sync users and groups from Runway to Google Play Console',
        requiredCapabilities: [['appStore']],
        supportedPlatforms: ['ios', 'tvos', 'android'],
        integrationInstallationIds: [],
        platform: 'android',
        appStoreType: 'GooglePlay',
        unsupportedIntegrations: ['amazon'],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: null,
        description: 'Runway will delete version-specific release branches when the release is complete and tagged.',
        enabled: true,
        id: 'AutoDeleteReleaseBranch',
        stepConfigId: null,
        type: 'AutoDeleteReleaseBranch',
        isNew: true,
        isBeta: false,
        name: 'Delete version-specific release branches at the end of the release cycle',
        requiredCapabilities: [['versionControl']],
        supportedPlatforms: ['ios', 'tvos', 'android'],
        integrationInstallationIds: [],
        platform: 'android',
        appStoreType: 'GooglePlay',
        unsupportedIntegrations: [],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: {
            daysToPercent: [1, 5, 10, 100],
            autoStagedRolloutBlackoutTimezone: 'America/Los_Angeles',
        },
        description:
            'Runway will increase the staged rollout percentage over the course of several days. You can optionally specify blackout dates and times on which your staged rollout will not increment.',
        enabled: true,
        id: 'AutoStagedRollout',
        stepConfigId: null,
        type: 'AutoStagedRollout',
        isNew: true,
        isBeta: false,
        name: 'Customized staged rollouts',
        requiredCapabilities: [['appStore']],
        supportedPlatforms: ['ios', 'tvos', 'android'],
        integrationInstallationIds: [],
        platform: 'android',
        appStoreType: 'GooglePlay',
        unsupportedIntegrations: [],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: { userGroupHandle: '' },
        description:
            'Runway will create and keep updated a Slack user group whose members are the release pilot for the active/next-up release. Specify the user group handle (e.g. runway-active-pilot) in the settings below.',
        enabled: false,
        id: 'AutoSyncSlackPilotUserGroup',
        stepConfigId: null,
        type: 'AutoSyncSlackPilotUserGroup',
        isNew: true,
        isBeta: false,
        name: 'Sync Slack pilot user group',
        requiredCapabilities: [['notifications']],
        supportedPlatforms: ['ios', 'android', 'ios-sdk', 'android-sdk', 'react-native-ota', 'tvos'],
        integrationInstallationIds: [],
        unsupportedIntegrations: ['microsoft-teams'],
        platform: 'android',
        appStoreType: 'GooglePlay',
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: null,
        description:
            'Runway will add and remove users to Slack groups based on their user groups in Runway. You can configure the mapping of user groups to Slack groups from App settings > Team > Slack groups sync',
        enabled: true,
        id: 'AutoSyncSlackGroups',
        stepConfigId: null,
        type: 'AutoSyncSlackGroups',
        isNew: true,
        isBeta: false,
        name: 'Sync Slack group members based on user groups',
        requiredCapabilities: [['notifications']],
        supportedPlatforms: ['ios', 'android', 'ios-sdk', 'android-sdk', 'react-native-ota', 'tvos'],
        integrationInstallationIds: [],
        platform: 'android',
        appStoreType: 'GooglePlay',
        unsupportedIntegrations: ['microsoft-teams'],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: {
            strategy: 'AutoCreateTestRunsWithoutTestPlans',
            testRunsFilterSettings: [],
            testPlansFilterSettings: [],
        },
        description: `Runway will create test runs (and if needed, test plans) for each new release when it's kicked off. The test runs and test plans from the previous release will be used as a template for test run and test plan creation.`,
        enabled: true,
        id: 'AutoCreateTestRuns',
        stepConfigId: null,
        type: 'AutoCreateTestRuns',
        isNew: true,
        isBeta: false,
        name: 'Create regression test runs',
        requiredCapabilities: [['regressionTesting']],
        supportedPlatforms: ['ios', 'android', 'ios-sdk', 'android-sdk', 'react-native-ota', 'tvos'],
        integrationInstallationIds: [],
        platform: 'android',
        appStoreType: 'GooglePlay',
        unsupportedIntegrations: ['xray'],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: {
            strategy: 'localResign',
            shouldAutoSubmit: false,
            abandonRollbackMonitoringConditions: [],
            abandonRollbackAdoptionThreshold: null,
            abandonRollbackPhasedReleaseDayThreshold: null,
            abandonRollbackStagedRolloutPercentageThreshold: null,
        },
        description:
            'Runway will re-sign, upload and (optionally) submit the previous stable build to have ready in case your latest release needs to be pulled back.',
        enabled: true,
        id: 'AutoPrepareRollbackRelease',
        stepConfigId: null,
        type: 'AutoPrepareRollbackRelease',
        isNew: true,
        isBeta: false,
        name: 'Prepare rollback releases',
        requiredCapabilities: [['appStore']],
        supportedPlatforms: ['ios', 'android', 'wearos', 'tvos'],
        integrationInstallationIds: [],
        platform: 'android',
        appStoreType: 'GooglePlay',
        unsupportedIntegrations: [],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        id: 'AutoCherryPickLocalizableStrings',
        stepConfigId: null,
        type: 'AutoCherryPickLocalizableStrings',
        isNew: true,
        isBeta: true,
        name: 'Update translations on the release branch from the working branch',
        description:
            'Runway will update translations on the release branch by pulling over new translations from the working branch as needed.',
        enabled: true,
        requiredCapabilities: [['translations'], ['versionControl']],
        supportedPlatforms: ['ios', 'android', 'wearos', 'tvos'],
        integrationInstallationIds: [],
        platform: 'android',
        appStoreType: 'GooglePlay',
        unsupportedIntegrations: [],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        id: 'AutoSyncTranslations',
        stepConfigId: null,
        type: 'AutoSyncTranslations',
        isNew: true,
        isBeta: true,
        name: 'Sync localizable string translations',
        description: 'Runway will upload keys for translation to the provider and export them when ready.',
        enabled: true,
        requiredCapabilities: [['translations'], ['versionControl']],
        supportedPlatforms: ['ios', 'android', 'wearos', 'tvos'],
        integrationInstallationIds: [],
        platform: 'android',
        appStoreType: 'GooglePlay',
        unsupportedIntegrations: [],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        id: 'AutoSyncMetadataTranslations',
        stepConfigId: null,
        type: 'AutoSyncMetadataTranslations',
        isNew: true,
        isBeta: true,
        name: 'Sync release metadata translations',
        description:
            'Runway will upload release metadata for translation if needed and export translated metadata to Play Console when ready.',
        enabled: true,
        requiredCapabilities: [['translations'], ['appStore']],
        supportedPlatforms: ['ios', 'android', 'wearos', 'tvos'],
        integrationInstallationIds: [],
        platform: 'android',
        appStoreType: 'GooglePlay',
        unsupportedIntegrations: [],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        id: 'AutoSyncMetadataFromVCS',
        stepConfigId: null,
        type: 'AutoSyncMetadataFromVCS',
        isNew: true,
        isBeta: true,
        name: 'Sync release metadata from version control',
        description:
            'Runway will read release metadata from files in your repo and automatically sync metadata to your release in Play Console.',
        enabled: true,
        requiredCapabilities: [['versionControl'], ['appStore']],
        supportedPlatforms: ['ios', 'android', 'wearos', 'tvos'],
        integrationInstallationIds: [],
        platform: 'android',
        appStoreType: 'GooglePlay',
        unsupportedIntegrations: [],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: null,
        description: 'Runway will automatically translate any non-English Play Store reviews into English.',
        enabled: true,
        id: 'AutoTranslateUserReviews',
        stepConfigId: null,
        type: 'AutoTranslateUserReviews',
        isNew: true,
        isBeta: true,
        name: 'Translate Play Store reviews',
        requiredCapabilities: [['appStore']],
        supportedPlatforms: ['ios', 'android', 'wearos', 'tvos'],
        integrationInstallationIds: [],
        platform: 'android',
        appStoreType: 'GooglePlay',
        unsupportedIntegrations: [],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: null,
        description:
            'Runway will post comments on your project management tickets with build info for builds that contain associated code.',
        enabled: true,
        id: 'AutoPostBuildInfoOnPMTickets',
        stepConfigId: null,
        type: 'AutoPostBuildInfoOnPMTickets',
        isNew: true,
        isBeta: true,
        name: 'Post build info to project management tickets',
        requiredCapabilities: [['buildSystem', 'featureManagement']],
        supportedPlatforms: ['ios', 'tvos', 'android', 'android-sdk', 'ios-sdk', 'react-native-ota'],
        integrationInstallationIds: [],
        platform: 'android',
        appStoreType: 'GooglePlay',
        unsupportedIntegrations: ['asana', 'azure-it', 'linear', 'pivotal', 'shortcut', 'monday'],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: null,
        description:
            'Runway will update all issue tracking tickets associated with the release to the configured status upon release completion.',
        enabled: true,
        id: 'AutoTransitionIssueTrackingTickets',
        stepConfigId: null,
        type: 'AutoTransitionIssueTrackingTickets',
        isNew: false,
        isBeta: false,
        name: 'Update status of issue tracking tickets upon release completion',
        requiredCapabilities: [['appStore']],
        supportedPlatforms: ['ios', 'tvos', 'android', 'android-sdk', 'ios-sdk', 'react-native-ota'],
        integrationInstallationIds: [],
        platform: 'android',
        appStoreType: 'GooglePlay',
        unsupportedIntegrations: [],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: null,
        description:
            'Runway will distribute a release summary via email to additional recipients at the end of each release cycle. You can configure the release summary template from App settings > Custom strings, and you can make changes to the release summary for a specific release from the Release step up until the release has gone live. By default, release summaries will be sent to all app users.',
        enabled: true,
        id: 'AutoDistributeReleaseSummary',
        stepConfigId: null,
        type: 'AutoDistributeReleaseSummary',
        isNew: false,
        isBeta: false,
        name: 'Distribute release summary to additional recipients at the end of each release cycle',
        requiredCapabilities: [['appStore']],
        supportedPlatforms: ['ios', 'tvos', 'android', 'android-sdk', 'ios-sdk', 'react-native-ota'],
        integrationInstallationIds: [],
        platform: 'android',
        appStoreType: 'GooglePlay',
        unsupportedIntegrations: [],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
];

export const iosAutomations: RecursiveStringifyEnums<Automation[]> = [
    {
        id: 'AutoResignRollbackBuild',
        type: AutomationType.AutoResignRollbackBuild,
        stepConfigId: null,
        name: 'Re-sign build for rollbacks',
        description:
            "Runway will take the build from the source version, bump the version, update the build number, and re-sign it so it's ready for app store upload.",
        additionalInfo: null,
        requiredCapabilities: [['buildSystem'], ['appStore']],
        unsupportedIntegrations: ['huawei', 'samsung'],
        supportedPlatforms: ['ios', 'tvos', 'android', 'wearos', 'android-tv'],
        integrationInstallationIds: [],
        platform: 'ios',
        appStoreType: 'AppStoreConnect',
        enabled: true,
        data: null,
        isNew: false,
        isBeta: true,
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: {
            strategy: AutoCherryPickStrategy.Cumulative,
            shouldNotifyPRAuthors: false,
            token: '[fix-{version}]',
            allowFixesWithToken: false,
            requiresFixApprovalToCherryPick: false,
        },
        description:
            'For any merged PR whose title contains the text pattern specified below, Runway will cherry-pick its commits from the working branch to the relevant release branch. Note that Runway will always open a PR containing the cherry-picked commits and targeting the release branch.',
        enabled: true,
        id: 'AutoCherryPickFromWorkingBranch',
        stepConfigId: null,
        type: 'AutoCherryPickFromWorkingBranch',
        isNew: false,
        isBeta: false,
        name: 'Pull (cherry-pick) fixes into the release',
        requiredCapabilities: [['versionControl']],
        supportedPlatforms: ['ios', 'android', 'ios-sdk', 'android-sdk', 'react-native-ota', 'tvos'],
        integrationInstallationIds: [],
        platform: 'ios',
        appStoreType: 'AppStoreConnect',
        unsupportedIntegrations: [],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: {
            track: null,
        },
        description:
            'Based on your selection below, Runway will select a build for the release version, and select it for release in App Store Connect. Note that there may be a processing delay of a few minutes.',
        enabled: true,
        id: 'AutoSelectLatestBuild',
        stepConfigId: null,
        type: 'AutoSelectLatestBuild',
        isNew: false,
        isBeta: false,
        name: 'Select build for App Store Connect',
        requiredCapabilities: [['appStore']],
        supportedPlatforms: ['ios', 'tvos', 'android'],
        integrationInstallationIds: [],
        platform: 'ios',
        appStoreType: 'AppStoreConnect',
        unsupportedIntegrations: [],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: null,
        description:
            'Runway will create a new version of your app in App Store Connect once the next release is being prepared in Runway.',
        enabled: true,
        id: 'AutoCreateAppStoreVersions',
        stepConfigId: null,
        type: 'AutoCreateAppStoreVersions',
        isNew: false,
        isBeta: false,
        name: 'Create and sync App Store Connect versions as needed to match current Runway release',
        requiredCapabilities: [['appStore']],
        supportedPlatforms: ['ios', 'tvos', 'android'],
        integrationInstallationIds: [],
        platform: 'ios',
        appStoreType: 'AppStoreConnect',
        unsupportedIntegrations: [],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: null,
        description: 'Runway will apply beta testing notes to new beta builds as they become available.',
        enabled: false,
        id: 'AutoApplyBetaBuildNotes',
        stepConfigId: null,
        type: 'AutoApplyBetaBuildNotes',
        isNew: false,
        isBeta: false,
        name: 'Apply beta testing notes to new beta builds',
        requiredCapabilities: [['betaTesting']],
        supportedPlatforms: ['ios', 'android', 'ios-sdk', 'android-sdk', 'react-native-ota', 'tvos'],
        integrationInstallationIds: [],
        platform: 'ios',
        appStoreType: 'AppStoreConnect',
        unsupportedIntegrations: ['amazon'],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: null,
        description:
            'Runway will download all artifacts generated from your build workflow and provide direct download links to them from the Runway interface. Additionally, Runway will include artifact information in Slack build success notifications, including a link to find those downloads in Runway.',
        enabled: true,
        id: 'AutoDownloadArtifacts',
        stepConfigId: null,
        type: 'AutoDownloadArtifacts',
        isNew: false,
        isBeta: false,
        name: 'Enable artifact downloads',
        requiredCapabilities: [['buildSystem']],
        supportedPlatforms: ['ios', 'tvos', 'android'],
        integrationInstallationIds: [],
        platform: 'ios',
        appStoreType: 'AppStoreConnect',
        unsupportedIntegrations: [
            'appcenter-ci',
            'apple-ci',
            'azure-ci',
            'buildkite',
            'codemagic',
            'generic-ci',
            'jenkins',
            'travis',
        ],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: null,
        description:
            'When tagging your production release commit, Runway will attach all artifacts generated from your active production build workflow as assets on your release record.',
        enabled: true,
        id: 'AutoUploadReleaseAssets',
        stepConfigId: null,
        type: 'AutoUploadReleaseAssets',
        isNew: false,
        isBeta: false,
        name: 'Attach build artifacts to VCS release record when tagging',
        requiredCapabilities: [['buildSystem'], ['versionControl']],
        supportedPlatforms: ['ios', 'tvos', 'android'],
        integrationInstallationIds: [],
        platform: 'ios',
        appStoreType: 'AppStoreConnect',
        unsupportedIntegrations: [
            'appcenter-ci',
            'apple-ci',
            'azure-ci',
            'buildkite',
            'codemagic',
            'generic-ci',
            'jenkins',
            'travis',
            'bitbucket',
            'gitlab-vcs',
        ],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: {
            uploadRule: '',
            uploadWorkflowSettings: null,
        },
        description: 'Runway will upload build artifacts for distribution according to the configured rules.',
        enabled: false,
        id: 'AutoUploadBinaryArtifactToAppStore',
        stepConfigId: null,
        type: 'AutoUploadBinaryArtifactToAppStore',
        isNew: true,
        isBeta: false,
        name: 'Upload build artifacts for distribution',
        requiredCapabilities: [['appStore', 'betaTesting'], ['buildSystem']],
        supportedPlatforms: ['ios', 'tvos', 'android'],
        integrationInstallationIds: [],
        platform: 'ios',
        appStoreType: 'AppStoreConnect',
        unsupportedIntegrations: [
            'appcenter-ci',
            'apple-ci',
            'azure-ci',
            'buildkite',
            'codemagic',
            'generic-ci',
            'jenkins',
            'travis',
            'appcenter-beta',
            'firebase-beta',
        ],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: {
            uploadWorkflowSettings: null,
        },
        description:
            'Runway will automatically upload dSYM files from build artifacts to your stability monitoring platform.',
        enabled: false,
        id: 'AutoUploadDsymsToStabilityMonitoring',
        stepConfigId: null,
        type: 'AutoUploadDsymsToStabilityMonitoring',
        isNew: false,
        isBeta: false,
        name: 'Upload dSYMs to stability monitoring',
        requiredCapabilities: [['buildSystem'], ['stabilityMonitoring']],
        supportedPlatforms: ['ios', 'tvos'],
        integrationInstallationIds: [],
        platform: 'ios',
        appStoreType: 'AppStoreConnect',
        unsupportedIntegrations: [
            'appcenter-ci',
            'apple-ci',
            'azure-ci',
            'buildkite',
            'codemagic',
            'generic-ci',
            'jenkins',
            'travis',
            'google',
            'amazon',
        ],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: null,
        description:
            'Runway will mark releases as completed in your project management tool at the end of each release cycle.',
        enabled: true,
        id: 'AutoCompleteIssueTrackingReleases',
        stepConfigId: null,
        type: 'AutoCompleteIssueTrackingReleases',
        isNew: false,
        isBeta: false,
        name: 'Mark releases as completed in project management tool',
        requiredCapabilities: [['featureManagement']],
        supportedPlatforms: ['ios', 'android', 'ios-sdk', 'android-sdk', 'react-native-ota', 'tvos'],
        integrationInstallationIds: [],
        platform: 'ios',
        appStoreType: 'AppStoreConnect',
        unsupportedIntegrations: ['asana', 'azure-it', 'linear', 'pivotal', 'shortcut'],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: null,
        description:
            "Runway will apply 'What's New' text to new versions created in App Store Connect. You can make updates to the text as needed before you submit your update for review.",
        enabled: true,
        id: 'AutoApplyMetadata',
        stepConfigId: null,
        type: 'AutoApplyMetadata',
        isNew: false,
        isBeta: false,
        name: "Apply 'What's New' text to new releases in App Store Connect",
        requiredCapabilities: [['appStore']],
        supportedPlatforms: ['ios', 'tvos', 'android'],
        integrationInstallationIds: [],
        platform: 'ios',
        appStoreType: 'AppStoreConnect',
        unsupportedIntegrations: [],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: null,
        description:
            'Runway will create a new release record for your next version when your current release is kicked off. This will assign a release pilot and provide a selectable upcoming release via the versions timeline in App Home.',
        enabled: true,
        id: 'AutoCreateRunwayReleases',
        stepConfigId: null,
        type: 'AutoCreateRunwayReleases',
        isNew: false,
        isBeta: false,
        name: 'Create upcoming version in Runway when current release is kicked off',
        requiredCapabilities: [],
        supportedPlatforms: ['ios', 'android', 'ios-sdk', 'android-sdk', 'react-native-ota', 'tvos'],
        integrationInstallationIds: [],
        platform: 'ios',
        appStoreType: 'AppStoreConnect',
        unsupportedIntegrations: [],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: {
            fallbackToHeadIfNeeded: false,
        },
        description:
            'Runway will tag your production release commit at the end of each release cycle, and generate a summary of all work going out with the release.',
        enabled: true,
        id: 'AutoTagReleases',
        stepConfigId: null,
        type: 'AutoTagReleases',
        isNew: false,
        isBeta: false,
        name: 'Tag releases at the end of the release cycle',
        requiredCapabilities: [['appStore'], ['buildSystem'], ['versionControl']],
        supportedPlatforms: ['ios', 'android', 'ios-sdk', 'android-sdk', 'react-native-ota', 'tvos'],
        integrationInstallationIds: [],
        platform: 'ios',
        appStoreType: 'AppStoreConnect',
        unsupportedIntegrations: [],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: null,
        description:
            'Runway creates pull requests to fulfill certain tasks (e.g. bumping version in code). If enabled, Runway will attempt to merge these PRs automatically, once any status checks or approvals are satisfied.',
        enabled: true,
        id: 'AutoMergeRunwayPRs',
        stepConfigId: null,
        type: 'AutoMergeRunwayPRs',
        isNew: false,
        isBeta: false,
        name: 'Merge pull requests opened by Runway',
        requiredCapabilities: [['versionControl']],
        supportedPlatforms: ['ios', 'android', 'ios-sdk', 'android-sdk', 'react-native-ota', 'tvos'],
        integrationInstallationIds: [],
        platform: 'ios',
        appStoreType: 'AppStoreConnect',
        unsupportedIntegrations: [],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: {
            strategy: 'onReleaseCompletion',
        },
        description:
            'Runway will backmerge changes from the release branch to the working branch (and any other upcoming release branches).',
        enabled: false,
        id: 'AutoBackmergeReleaseBranches',
        stepConfigId: null,
        type: 'AutoBackmergeReleaseBranches',
        isNew: false,
        isBeta: false,
        name: 'Backmerge changes from the release branch',
        requiredCapabilities: [['versionControl']],
        supportedPlatforms: ['ios', 'android', 'ios-sdk', 'android-sdk', 'react-native-ota', 'tvos'],
        integrationInstallationIds: [],
        platform: 'ios',
        appStoreType: 'AppStoreConnect',
        unsupportedIntegrations: [],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: null,
        description:
            'Runway will add appropriate labels and/or fix versions when they are missing from tickets that are associated with the release. These label and fix version patterns are defined in App Settings.',
        enabled: true,
        id: 'AutoAddMissingFAs',
        stepConfigId: null,
        type: 'AutoAddMissingFAs',
        isNew: false,
        isBeta: false,
        name: 'Add missing labels or fix versions to tickets',
        requiredCapabilities: [['featureManagement'], ['versionControl']],
        supportedPlatforms: ['ios', 'android', 'ios-sdk', 'android-sdk', 'react-native-ota', 'tvos'],
        integrationInstallationIds: [],
        platform: 'ios',
        appStoreType: 'AppStoreConnect',
        unsupportedIntegrations: [],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: {
            allowKickoffWhenPreviousNotComplete: true,
            shouldCommitAndPushDirectlyToTargetBranch: false,
        },
        description:
            'Runway will create a release branch off of your working branch for the next release on the scheduled date.',
        enabled: true,
        id: 'AutoKickoff',
        stepConfigId: null,
        type: 'AutoKickoff',
        isNew: false,
        isBeta: false,
        name: 'Kick off release on target date',
        requiredCapabilities: [['versionControl']],
        supportedPlatforms: ['ios', 'android', 'ios-sdk', 'android-sdk', 'react-native-ota', 'tvos'],
        integrationInstallationIds: [],
        platform: 'ios',
        appStoreType: 'AppStoreConnect',
        unsupportedIntegrations: [],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: {
            submitStrategy: 'onTargetDate',
        },
        description: 'Runway will submit your selected build for review if all previous steps are ready (green).',
        enabled: true,
        id: 'AutoSubmit',
        stepConfigId: null,
        type: 'AutoSubmit',
        isNew: false,
        isBeta: false,
        name: 'Submit app for review',
        requiredCapabilities: [['appStore']],
        supportedPlatforms: ['ios', 'tvos', 'android'],
        integrationInstallationIds: [],
        platform: 'ios',
        appStoreType: 'AppStoreConnect',
        unsupportedIntegrations: [],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: {
            shouldCommitAndPushDirectlyToTargetBranch: false,
            strategy: 'AutoReleaseOnDate',
        },
        description:
            'Runway will release your app update (or start your phased release, if applicable) on the scheduled date if all previous steps are ready (green).',
        enabled: true,
        id: 'AutoRelease',
        stepConfigId: null,
        type: 'AutoRelease',
        isNew: false,
        isBeta: false,
        name: 'Release app',
        requiredCapabilities: [['appStore']],
        supportedPlatforms: ['ios', 'tvos'],
        integrationInstallationIds: [],
        platform: 'ios',
        appStoreType: 'AppStoreConnect',
        unsupportedIntegrations: ['google', 'amazon'],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: null,
        description:
            'Runway will submit any new builds uploaded to TestFlight for beta review as soon as they finish processing.',
        enabled: false,
        id: 'AutoSubmitBetaBuilds',
        stepConfigId: null,
        type: 'AutoSubmitBetaBuilds',
        isNew: false,
        isBeta: false,
        name: 'Submit new builds for beta review',
        requiredCapabilities: [['betaTesting']],
        supportedPlatforms: ['ios', 'tvos'],
        integrationInstallationIds: [],
        platform: 'ios',
        appStoreType: 'AppStoreConnect',
        unsupportedIntegrations: ['google-beta', 'appcenter-beta', 'firebase-beta'],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: null,
        description:
            'Runway will assign any new builds uploaded to TestFlight to your default beta groups for testing. External testers will have access once the build has been reviewed.',
        enabled: false,
        id: 'AutoAssignBetaBuilds',
        stepConfigId: null,
        type: 'AutoAssignBetaBuilds',
        isNew: false,
        isBeta: false,
        name: 'Assign beta testing builds to default testing groups',
        requiredCapabilities: [['betaTesting']],
        supportedPlatforms: ['ios', 'tvos'],
        integrationInstallationIds: [],
        platform: 'ios',
        appStoreType: 'AppStoreConnect',
        unsupportedIntegrations: ['google-beta'],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: null,
        description:
            'When submitting a new release for review, Runway will apply your default file attachment provided for reviewers (as defined in App Settings) to new versions created in App Store Connect.',
        enabled: false,
        id: 'AutoApplyReviewAttachments',
        stepConfigId: null,
        type: 'AutoApplyReviewAttachments',
        isNew: false,
        isBeta: false,
        name: 'Apply default review attachment to new app versions in App Store Connect',
        requiredCapabilities: [['appStore']],
        supportedPlatforms: ['ios', 'tvos'],
        integrationInstallationIds: [],
        platform: 'ios',
        appStoreType: 'AppStoreConnect',
        unsupportedIntegrations: ['google', 'amazon'],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: {
            shouldCommitAndPushDirectlyToTargetBranch: false,
            strategy: 'AutoBumpPostKickoffToNextVersion',
        },
        description:
            'Runway will bump the version on your working branch or release branch according to the strategy set for the app.',
        enabled: true,
        id: 'AutoBumpVersion',
        stepConfigId: null,
        type: 'AutoBumpVersion',
        isNew: false,
        isBeta: false,
        name: 'Bump version',
        requiredCapabilities: [['versionControl']],
        supportedPlatforms: ['ios', 'tvos', 'android'],
        integrationInstallationIds: [],
        platform: 'ios',
        appStoreType: 'AppStoreConnect',
        unsupportedIntegrations: [],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: {
            adoptionThreshold: 10,
            monitoringConditions: [],
            phasedReleaseDayThreshold: null,
            stagedRolloutPercentageThreshold: null,
        },
        description:
            'Runway will release your update to all users early if all of your defined conditions have been met, and your selected health metrics stay within your defined health threshold.',
        enabled: true,
        id: 'AutoPromoteStablePhasedReleases',
        stepConfigId: null,
        type: 'AutoPromoteStablePhasedReleases',
        isNew: false,
        isBeta: false,
        name: 'Release stable phased releases to 100% of users',
        requiredCapabilities: [['appStore', 'productAnalytics', 'stabilityMonitoring']],
        supportedPlatforms: ['ios', 'tvos', 'android'],
        integrationInstallationIds: [],
        platform: 'ios',
        appStoreType: 'AppStoreConnect',
        unsupportedIntegrations: ['amazon'],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: {
            adoptionThreshold: 10,
            monitoringConditions: [],
            phasedReleaseDayThreshold: null,
            stagedRolloutPercentageThreshold: null,
        },
        description:
            'Runway will pause your phased release if all of your defined conditions have been met, and any of your selected health metrics become unhealthy.',
        enabled: true,
        id: 'AutoHaltUnstablePhasedReleases',
        stepConfigId: null,
        type: 'AutoHaltUnstablePhasedReleases',
        isNew: false,
        isBeta: false,
        name: 'Halt unstable phased releases',
        requiredCapabilities: [['appStore', 'productAnalytics', 'stabilityMonitoring']],
        supportedPlatforms: ['ios', 'tvos', 'android'],
        integrationInstallationIds: [],
        platform: 'ios',
        appStoreType: 'AppStoreConnect',
        unsupportedIntegrations: ['amazon'],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: null,
        description:
            "Runway will automatically create new Slack channel(s) for each release. If a tokenized channel name (e.g. release-{version}) is configured as either the 'Primary channel' for the Slack integration, or as a channel override for any notification in settings, Runway will create a new channel matching the tokenized channel name for each active release if needed.",
        enabled: false,
        id: 'AutoCreateReleaseChannels',
        stepConfigId: null,
        type: 'AutoCreateReleaseChannels',
        isNew: false,
        isBeta: false,
        name: 'Create release-specific Slack or Microsoft Teams channel(s)',
        requiredCapabilities: [['notifications']],
        supportedPlatforms: ['ios', 'android', 'ios-sdk', 'android-sdk', 'react-native-ota', 'tvos'],
        integrationInstallationIds: [],
        platform: 'ios',
        appStoreType: 'AppStoreConnect',
        unsupportedIntegrations: [],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: null,
        description: 'Runway will trigger the configured CI build workflow after release is kicked off.',
        enabled: false,
        id: 'AutoKickoffTriggerWorkflow',
        stepConfigId: null,
        type: 'AutoKickoffTriggerWorkflow',
        isNew: false,
        isBeta: false,
        name: 'Trigger workflow after release kickoff',
        requiredCapabilities: [['buildSystem'], ['versionControl']],
        supportedPlatforms: ['ios', 'android', 'ios-sdk', 'android-sdk', 'react-native-ota', 'tvos'],
        integrationInstallationIds: [],
        platform: 'ios',
        appStoreType: 'AppStoreConnect',
        unsupportedIntegrations: [],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: {
            allowDeleteUsers: false,
        },
        description:
            'Users in Runway will automatically be invited to App Store Connect, with the appropriate user groups assigned. Please note: this automation requires that the App Store Connect API key in use by Runway has the Admin user group.',
        enabled: false,
        id: 'AutoSyncAppStoreUsers',
        stepConfigId: null,
        type: 'AutoSyncAppStoreUsers',
        isNew: false,
        isBeta: false,
        name: 'Sync users and groups from Runway to App Store Connect',
        requiredCapabilities: [['appStore']],
        supportedPlatforms: ['ios', 'tvos', 'android'],
        integrationInstallationIds: [],
        platform: 'ios',
        appStoreType: 'AppStoreConnect',
        unsupportedIntegrations: ['amazon'],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: null,
        description: 'Runway will delete version-specific release branches when the release is complete and tagged.',
        enabled: true,
        id: 'AutoDeleteReleaseBranch',
        stepConfigId: null,
        type: 'AutoDeleteReleaseBranch',
        isNew: true,
        isBeta: false,
        name: 'Delete version-specific release branches at the end of the release cycle',
        requiredCapabilities: [['versionControl']],
        supportedPlatforms: ['ios', 'tvos', 'android'],
        integrationInstallationIds: [],
        platform: 'ios',
        appStoreType: 'AppStoreConnect',
        unsupportedIntegrations: [],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: {
            autoStagedRolloutBlackoutTimezone: 'America/Los_Angeles',
        },
        description:
            'Customize phased rollouts with blackout dates and times – Runway will pause and resume phased rollouts to avoid phased rollout percentage increments on the blackout dates and times you specify.',
        enabled: true,
        id: 'AutoStagedRollout',
        stepConfigId: null,
        type: 'AutoStagedRollout',
        isNew: false,
        isBeta: false,
        name: 'Customized phased releases',
        requiredCapabilities: [['appStore']],
        supportedPlatforms: ['ios', 'tvos', 'android'],
        integrationInstallationIds: [],
        platform: 'ios',
        appStoreType: 'AppStoreConnect',
        unsupportedIntegrations: [],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: { userGroupHandle: '' },
        description:
            'Runway will create and keep updated a Slack user group whose members are the release pilot for the active/next-up release. Specify the user group handle (e.g. runway-active-pilot) in the settings below.',
        enabled: false,
        id: 'AutoSyncSlackPilotUserGroup',
        stepConfigId: null,
        type: 'AutoSyncSlackPilotUserGroup',
        isNew: true,
        isBeta: false,
        name: 'Sync Slack pilot user group',
        requiredCapabilities: [['notifications']],
        supportedPlatforms: ['ios', 'android', 'ios-sdk', 'android-sdk', 'react-native-ota', 'tvos'],
        integrationInstallationIds: [],
        unsupportedIntegrations: ['microsoft-teams'],
        platform: 'ios',
        appStoreType: 'AppStoreConnect',
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: null,
        description:
            'Runway will add and remove users to Slack groups based on their user groups in Runway. You can configure the mapping of user groups to Slack groups from App settings > Team > Slack groups sync',
        enabled: true,
        id: 'AutoSyncSlackGroups',
        stepConfigId: null,
        type: 'AutoSyncSlackGroups',
        isNew: true,
        isBeta: false,
        name: 'Sync Slack group members based on user groups',
        requiredCapabilities: [['notifications']],
        supportedPlatforms: ['ios', 'android', 'ios-sdk', 'android-sdk', 'react-native-ota', 'tvos'],
        integrationInstallationIds: [],
        platform: 'ios',
        appStoreType: 'AppStoreConnect',
        unsupportedIntegrations: ['microsoft-teams'],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: {
            strategy: 'localResign',
            shouldAutoSubmit: false,
            abandonRollbackMonitoringConditions: [],
            abandonRollbackAdoptionThreshold: null,
            abandonRollbackPhasedReleaseDayThreshold: null,
            abandonRollbackStagedRolloutPercentageThreshold: null,
        },
        description:
            'Runway will re-sign, upload and (optionally) submit the previous stable build to have ready in case your latest release needs to be pulled back.',
        enabled: true,
        id: 'AutoPrepareRollbackRelease',
        stepConfigId: null,
        type: 'AutoPrepareRollbackRelease',
        isNew: true,
        isBeta: false,
        name: 'Prepare rollback releases',
        requiredCapabilities: [['appStore']],
        supportedPlatforms: ['ios', 'android', 'wearos', 'tvos'],
        integrationInstallationIds: [],
        platform: 'ios',
        appStoreType: 'AppStoreConnect',
        unsupportedIntegrations: [],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        id: 'AutoCherryPickLocalizableStrings',
        stepConfigId: null,
        type: 'AutoCherryPickLocalizableStrings',
        isNew: true,
        isBeta: true,
        name: 'Update translations on the release branch from the working branch',
        description:
            'Runway will update translations on the release branch by pulling over new translations from the working branch as needed.',
        enabled: true,
        requiredCapabilities: [['translations'], ['versionControl']],
        supportedPlatforms: ['ios', 'android', 'wearos', 'tvos'],
        integrationInstallationIds: [],
        platform: 'ios',
        appStoreType: 'AppStoreConnect',
        unsupportedIntegrations: [],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        id: 'AutoSyncTranslations',
        stepConfigId: null,
        type: 'AutoSyncTranslations',
        isNew: true,
        isBeta: true,
        name: 'Sync localizable string translations',
        description: 'Runway will upload keys for translation to the provider and export them when ready.',
        enabled: true,
        requiredCapabilities: [['translations'], ['versionControl']],
        supportedPlatforms: ['ios', 'android', 'wearos', 'tvos'],
        integrationInstallationIds: [],
        platform: 'ios',
        appStoreType: 'AppStoreConnect',
        unsupportedIntegrations: [],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        name: 'Sync release metadata from version control',
        id: 'AutoSyncMetadataFromVCS',
        stepConfigId: null,
        type: 'AutoSyncMetadataFromVCS',
        description:
            'Runway will read release metadata from files in your repo and automatically sync metadata to your release in Play Console.',
        enabled: true,
        isNew: false,
        isBeta: false,
        requiredCapabilities: [['versionControl']],
        supportedPlatforms: ['android'],
        integrationInstallationIds: [],
        platform: 'ios',
        appStoreType: 'AppStoreConnect',
        unsupportedIntegrations: [],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        id: 'AutoSyncMetadataTranslations',
        stepConfigId: null,
        type: 'AutoSyncMetadataTranslations',
        isNew: true,
        isBeta: true,
        name: 'Sync release metadata translations',
        description:
            'Runway will upload release metadata for translation if needed and export translated metadata to App Store Connect when ready.',
        enabled: true,
        requiredCapabilities: [['translations'], ['appStore']],
        supportedPlatforms: ['ios', 'android', 'wearos', 'tvos'],
        integrationInstallationIds: [],
        platform: 'ios',
        appStoreType: 'AppStoreConnect',
        unsupportedIntegrations: [],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: {
            strategy: 'AutoCreateTestRunsWithoutTestPlans',
            testRunsFilterSettings: [],
            testPlansFilterSettings: [],
        },
        description: `Runway will create test runs (and if needed, test plans) for each new release when it's kicked off. The test runs and test plans from the previous release will be used as a template for test run and test plan creation.`,
        enabled: true,
        id: 'AutoCreateTestRuns',
        stepConfigId: null,
        type: 'AutoCreateTestRuns',
        isNew: true,
        isBeta: false,
        name: 'Create regression test runs',
        requiredCapabilities: [['regressionTesting']],
        supportedPlatforms: ['ios', 'android', 'ios-sdk', 'android-sdk', 'react-native-ota', 'tvos'],
        integrationInstallationIds: [],
        platform: 'ios',
        appStoreType: 'AppStoreConnect',
        unsupportedIntegrations: ['xray'],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: null,
        description: 'Runway will automatically translate any non-English App Store reviews into English.',
        enabled: true,
        id: 'AutoTranslateUserReviews',
        stepConfigId: null,
        type: 'AutoTranslateUserReviews',
        isNew: true,
        isBeta: true,
        name: 'Translate App Store reviews',
        requiredCapabilities: [['appStore']],
        supportedPlatforms: ['ios', 'android', 'wearos', 'tvos'],
        integrationInstallationIds: [],
        platform: 'ios',
        appStoreType: 'AppStoreConnect',
        unsupportedIntegrations: [],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: null,
        description:
            'Runway will post comments on your project management tickets with build info for builds that contain associated code.',
        enabled: true,
        id: 'AutoPostBuildInfoOnPMTickets',
        stepConfigId: null,
        type: 'AutoPostBuildInfoOnPMTickets',
        isNew: true,
        isBeta: true,
        name: 'Post build info to project management tickets',
        requiredCapabilities: [['buildSystem', 'featureManagement']],
        supportedPlatforms: ['ios', 'tvos', 'android', 'android-sdk', 'ios-sdk', 'react-native-ota'],
        integrationInstallationIds: [],
        platform: 'ios',
        appStoreType: 'AppStoreConnect',
        unsupportedIntegrations: ['asana', 'azure-it', 'linear', 'pivotal', 'shortcut', 'monday'],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: null,
        description:
            'Runway will update all issue tracking tickets associated with the release to the configured status upon release completion.',
        enabled: true,
        id: 'AutoTransitionIssueTrackingTickets',
        stepConfigId: null,
        type: 'AutoTransitionIssueTrackingTickets',
        isNew: false,
        isBeta: false,
        name: 'Update status of issue tracking tickets upon release completion',
        requiredCapabilities: [['appStore']],
        supportedPlatforms: ['ios', 'tvos', 'android', 'android-sdk', 'ios-sdk', 'react-native-ota'],
        integrationInstallationIds: [],
        platform: 'ios',
        appStoreType: 'AppStoreConnect',
        unsupportedIntegrations: [],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
    {
        data: null,
        description:
            'Runway will distribute a release summary via email to additional recipients at the end of each release cycle. You can configure the release summary template from App settings > Custom strings, and you can make changes to the release summary for a specific release from the Release step up until the release has gone live. By default, release summaries will be sent to all app users.',
        enabled: true,
        id: 'AutoDistributeReleaseSummary',
        stepConfigId: null,
        type: 'AutoDistributeReleaseSummary',
        isNew: false,
        isBeta: false,
        name: 'Distribute release summary to additional recipients at the end of each release cycle',
        requiredCapabilities: [['appStore']],
        supportedPlatforms: ['ios', 'tvos', 'android', 'android-sdk', 'ios-sdk', 'react-native-ota'],
        integrationInstallationIds: [],
        platform: 'ios',
        appStoreType: 'AppStoreConnect',
        unsupportedIntegrations: [],
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
];

export const workflowOnlyAutomations = (
    platform: AppPlatform,
    appStoreType: AppStoreType,
): RecursiveStringifyEnums<Automation[]> => [
    {
        id: 'AutoTriggerCIWorkflow',
        type: AutomationType.AutoTriggerCIWorkflow,
        stepConfigId: null,
        name: 'Trigger CI workflow',
        description:
            'Runway will trigger the configured CI workflow based on the configured settings relative to kickoff, submission, or release.',
        additionalInfo: null,
        requiredCapabilities: [['buildSystem']],
        unsupportedIntegrations: ['huawei', 'samsung'],
        supportedPlatforms: ['ios', 'tvos', 'android', 'wearos', 'android-tv'],
        integrationInstallationIds: [],
        platform,
        appStoreType,
        enabled: true,
        data: null,
        isNew: false,
        isBeta: true,
        relativeTimeTrigger: {
            enabled: false,
            timing: 'onTime',
            relativeDays: 0,
            relativeHours: 0,
            relativeMinutes: 0,
            lifecycleEventType: 'kickoff',
            waitForAllDestinations: true,
        },
        completedStepsTrigger: {
            enabled: false,
            hasCustomRequiredSteps: false,
            requiredSteps: [],
        },
    },
];
